import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { formatWorkedMinutes } from '../utils/attendanceStatus';
import {
  resolveWorkHoursCoverage,
  workHoursCoverageLabel,
} from '../utils/workHoursCoverage';

type Props = {
  workedMinutes: number | null;
  expectedMinutes: number | null;
  label?: string;
  /** Covered / Not Covered is final only after check-out. */
  showCoverage?: boolean;
};

function ringColors(progress: number, track: string, fill: string) {
  if (progress <= 0) {
    return {
      borderTopColor: track,
      borderRightColor: track,
      borderBottomColor: track,
      borderLeftColor: track,
    };
  }
  if (progress >= 1) {
    return {
      borderTopColor: fill,
      borderRightColor: fill,
      borderBottomColor: fill,
      borderLeftColor: fill,
    };
  }
  return {
    borderTopColor: progress > 0.12 ? fill : track,
    borderRightColor: progress > 0.37 ? fill : track,
    borderBottomColor: progress > 0.62 ? fill : track,
    borderLeftColor: progress > 0.87 ? fill : track,
  };
}

export function WorkHoursRing({
  workedMinutes,
  expectedMinutes,
  label = 'Work hours',
  showCoverage = true,
}: Props) {
  const palette = useAppColors();
  const worked = workedMinutes ?? 0;
  const coverage = showCoverage
    ? resolveWorkHoursCoverage(workedMinutes, expectedMinutes)
    : 'unknown';
  const coverageLabel = workHoursCoverageLabel(coverage);
  const progress =
    expectedMinutes !== null && expectedMinutes > 0
      ? Math.min(worked / expectedMinutes, 1)
      : 0;
  const fillColor =
    coverage === 'not_covered' ? palette.error : palette.success;
  const ringSize = 132;
  const borderWidth = 10;

  const ringStyle = useMemo(
    () => ringColors(progress, palette.border, fillColor),
    [fillColor, palette.border, progress],
  );

  return (
    <View
      accessibilityLabel={`${label}: ${formatWorkedMinutes(workedMinutes)}${
        coverageLabel ? `, ${coverageLabel}` : ''
      }`}
      style={styles.container}
    >
      <View
        style={[
          styles.ring,
          {
            width: ringSize,
            height: ringSize,
            borderRadius: ringSize / 2,
            borderWidth,
            borderColor: palette.border,
            ...ringStyle,
          },
        ]}
      >
        <View
          style={[
            styles.inner,
            {
              width: ringSize - borderWidth * 2 - 8,
              height: ringSize - borderWidth * 2 - 8,
              borderRadius: (ringSize - borderWidth * 2 - 8) / 2,
              backgroundColor: palette.surface,
            },
          ]}
        >
          <Text style={[styles.value, { color: palette.textPrimary }]}>
            {formatWorkedMinutes(workedMinutes)}
          </Text>
          <Text
            style={[
              styles.caption,
              {
                color:
                  coverage === 'covered'
                    ? palette.success
                    : coverage === 'not_covered'
                    ? palette.error
                    : palette.textMuted,
                fontWeight: coverageLabel ? '700' : '400',
              },
            ]}
          >
            {coverageLabel ?? label}
          </Text>
        </View>
      </View>
      <Text style={[styles.expected, { color: palette.textSecondary }]}>
        Expected {formatWorkedMinutes(expectedMinutes)}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { alignItems: 'center', gap: spacing.sm },
  ring: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  inner: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  value: {
    ...typography.sectionTitle,
    fontWeight: '700',
  },
  caption: { ...typography.caption },
  expected: { ...typography.caption },
});
